import React, { useState, useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, Typography } from "@mui/material";
import styled from "styled-components";
import { logError } from "../../shared/utils/logger.js";

// Fix for default marker icons in React-Leaflet
if (typeof window !== "undefined") {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  });
}

const LoadingContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const ErrorContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const ErrorText = styled(Typography)`
  font-size: 16px;
  color: #ff4d4f;
`;

const ErrorDetail = styled(Typography)`
  font-size: 14px;
  color: #666;
  text-align: center;
`;

// Component to handle map bounds fitting and refresh on drawer resize
const MapBoundsFitter = ({ bounds, drawerExpanded, autoFitBounds = true }) => {
  const map = useMap();

  // Helper function to check if map is fully initialized
  const isMapReady = () => {
    return map && map._loaded && map._mapPane && map._mapPane._leaflet_id;
  };

  useEffect(() => {
    // Ensure map is fully initialized before calling methods
    if (!isMapReady()) {
      // Wait a bit for map to initialize, then retry
      const timeoutId = setTimeout(() => {
        if (isMapReady() && bounds && autoFitBounds && bounds.length > 0) {
          try {
            const leafletBounds = L.latLngBounds(bounds);
            map.fitBounds(leafletBounds, {
              padding: [80, 80], // Increased padding for better visibility
              maxZoom: 15,
              animate: true, // Smooth transition
            });
          } catch (err) {
            logError("Error fitting map bounds", {
              error: err.message,
            });
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }

    if (bounds && autoFitBounds && bounds.length > 0) {
      try {
        const leafletBounds = L.latLngBounds(bounds);
        map.fitBounds(leafletBounds, {
          padding: [80, 80], // Increased padding for better visibility
          maxZoom: 15,
          animate: true, // Smooth transition
        });
      } catch (err) {
        logError("Error fitting map bounds", {
          error: err.message,
        });
      }
    }
  }, [map, bounds, autoFitBounds]);

  // Refresh map when drawer expands/contracts
  useEffect(() => {
    if (!drawerExpanded) return;

    const timeoutId = setTimeout(() => {
      // Double-check map is still valid before calling methods
      if (!isMapReady()) {
        logError("Map not ready for invalidateSize", {
          mapLoaded: map?._loaded,
          hasMapPane: !!map?._mapPane,
        });
        return;
      }

      try {
        map.invalidateSize();
        if (bounds && autoFitBounds && bounds.length > 0) {
          const leafletBounds = L.latLngBounds(bounds);
          map.fitBounds(leafletBounds, {
            padding: [80, 80], // Increased padding for better visibility
            maxZoom: 15,
            animate: true, // Smooth transition
          });
        }
      } catch (err) {
        logError("Error refreshing map", {
          error: err.message,
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [map, drawerExpanded, bounds, autoFitBounds]);

  return null;
};

/**
 * RouteMapLeaflet - Generic map component supporting multiple markers and routes
 * @param {Object} props
 * @param {string} props.startLocation - Start address (for backward compatibility)
 * @param {string} props.endLocation - End address (for backward compatibility)
 * @param {Object} props.pickupAddress - Structured pickup address with coordinates { address, coordinates: { lat, lng } }
 * @param {Object} props.deliveryAddress - Structured delivery address with coordinates { address, coordinates: { lat, lng } }
 * @param {Array} props.markers - Array of marker objects: { coords: [lat, lng], icon, color, label, popup, onClick }
 * @param {Array} props.route - Array of route coordinates: [[lat, lng], ...] (single route)
 * @param {Array} props.routes - Array of route objects: [{ route: [[lat, lng], ...], color?: string }] (multiple routes)
 * @param {boolean} props.drawerExpanded - Whether drawer is expanded (for refresh)
 * @param {boolean} props.autoFitBounds - Whether to auto-fit bounds (default: true)
 * @param {Function} props.onMarkerClick - Callback when marker is clicked
 */
const RouteMapLeaflet = ({
  startLocation,
  endLocation,
  pickupAddress,
  deliveryAddress,
  markers = [],
  route = null,
  routes = null, // Array of route objects: [{ route: [...], color?: string }]
  drawerExpanded = false,
  autoFitBounds = true,
  onMarkerClick,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [geocodedMarkers, setGeocodedMarkers] = useState([]);
  const [calculatedRoute, setCalculatedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Geocode addresses using Nominatim (free OpenStreetMap geocoding).
    // Restricted to South Africa (countrycodes=za) so vague/incomplete address
    // fragments (e.g. a lead saved with just "355 west") can't resolve to a
    // random match on another continent and produce a nonsensical route.
    const geocode = async (address) => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=za`,
          {
            headers: {
              "User-Agent": "Bubakii Logistics Platform",
            },
          }
        );
        const data = await response.json();
        if (data.length > 0) {
          return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        }
        return null;
      } catch (err) {
        logError("Geocoding error", {
          address,
          error: err.message,
        });
        return null;
      }
    };

    const loadMapData = async () => {
      setLoading(true);
      setError(null);

      try {
        // If markers are provided, use them directly (assumes coords are already set)
        if (markers.length > 0 && markers[0].coords) {
          setGeocodedMarkers(markers);
          setLoading(false);

          // Calculate route if coordinates available
          if (markers.length >= 2 && markers[0].coords && markers[1].coords) {
            const startPos = markers[0].coords;
            const endPos = markers[1].coords;

            const response = await fetch(
              `https://router.project-osrm.org/route/v1/driving/${startPos[1]},${startPos[0]};${endPos[1]},${endPos[0]}?overview=full&geometries=geojson`
            );
            const data = await response.json();

            if (data.code === "Ok" && data.routes && data.routes.length > 0) {
              const coordinates = data.routes[0].geometry.coordinates.map(
                (coord) => [coord[1], coord[0]]
              );
              setCalculatedRoute(coordinates);
            }
          }
          return;
        }

        // If either address was already flagged as needing more detail
        // (missing suburb/city/province), don't fall back to a blind
        // free-text geocode — it can resolve to an unrelated location on
        // another continent (this produced an "Impossible route between
        // points" OSRM error for a lead whose pickup address was "355 west").
        if (
          pickupAddress?.precisionInsufficient ||
          deliveryAddress?.precisionInsufficient
        ) {
          setError(
            "Pickup or delivery address needs more detail (suburb, city, or province) to show an accurate route."
          );
          setLoading(false);
          return;
        }

        // NEW: Use structured addresses with coordinates if available (no geocoding needed!)
        if (pickupAddress?.coordinates && deliveryAddress?.coordinates) {
          const startPos = [
            pickupAddress.coordinates.lat,
            pickupAddress.coordinates.lng,
          ];
          const endPos = [
            deliveryAddress.coordinates.lat,
            deliveryAddress.coordinates.lng,
          ];

          const startMarker = {
            coords: startPos,
            label: "Pickup",
            popup: pickupAddress.address || startLocation,
            icon: "red",
          };

          const endMarker = {
            coords: endPos,
            label: "Delivery",
            popup: deliveryAddress.address || endLocation,
            icon: "green",
          };

          setGeocodedMarkers([startMarker, endMarker]);

          // Get route using OSRM (coordinates already available, no geocoding needed)
          const response = await fetch(
            `https://router.project-osrm.org/route/v1/driving/${startPos[1]},${startPos[0]};${endPos[1]},${endPos[0]}?overview=full&geometries=geojson`
          );
          const data = await response.json();

          if (data.code === "Ok" && data.routes && data.routes.length > 0) {
            const coordinates = data.routes[0].geometry.coordinates.map(
              (coord) => [coord[1], coord[0]]
            );
            setCalculatedRoute(coordinates);
          } else {
            logError("Route calculation failed", {
              pickupAddress: pickupAddress.address,
              deliveryAddress: deliveryAddress.address,
            });
          }

          setLoading(false);
          return;
        }

        // Backward compatibility: geocode start/end locations if coordinates not available
        if (startLocation && endLocation) {
          const startPos = await geocode(startLocation);
          const endPos = await geocode(endLocation);

          if (!startPos || !endPos) {
            setError("Could not find coordinates for addresses");
            setLoading(false);
            return;
          }

          const startMarker = {
            coords: startPos,
            label: "Pickup",
            popup: startLocation,
            icon: "red",
          };

          const endMarker = {
            coords: endPos,
            label: "Delivery",
            popup: endLocation,
            icon: "green",
          };

          setGeocodedMarkers([startMarker, endMarker]);

          // Get route using OSRM
          const response = await fetch(
            `https://router.project-osrm.org/route/v1/driving/${startPos[1]},${startPos[0]};${endPos[1]},${endPos[0]}?overview=full&geometries=geojson`
          );
          const data = await response.json();

          if (data.code === "Ok" && data.routes && data.routes.length > 0) {
            const coordinates = data.routes[0].geometry.coordinates.map(
              (coord) => [coord[1], coord[0]]
            );
            setCalculatedRoute(coordinates);
          } else {
            logError("Route calculation failed", {
              startLocation,
              endLocation,
            });
          }
        } else {
          setError("No locations provided");
        }
      } catch (err) {
        logError("Route calculation error", {
          startLocation,
          endLocation,
          pickupAddress: pickupAddress?.address,
          deliveryAddress: deliveryAddress?.address,
          error: err.message,
        });
        setError("Error loading route");
      } finally {
        setLoading(false);
      }
    };

    if (
      pickupAddress?.coordinates ||
      deliveryAddress?.coordinates ||
      startLocation ||
      markers.length > 0
    ) {
      loadMapData();
    }
  }, [startLocation, endLocation, pickupAddress, deliveryAddress, markers]);

  // Calculate display data and bounds (must be before early returns for hooks)
  const displayMarkers =
    markers.length > 0 && markers[0].coords ? markers : geocodedMarkers;
  const displayRoute = route || calculatedRoute;
  const displayRoutes = useMemo(() => {
    return (
      routes ||
      (displayRoute ? [{ route: displayRoute, color: "#1890ff" }] : [])
    );
  }, [routes, displayRoute]);

  // Calculate bounds including both markers AND route coordinates for dynamic viewport
  const bounds = useMemo(() => {
    const allPoints = [];

    // Add marker coordinates
    if (displayMarkers.length > 0) {
      displayMarkers.forEach((m) => {
        if (m.coords && Array.isArray(m.coords) && m.coords.length >= 2) {
          allPoints.push(m.coords);
        }
      });
    }

    // Add route coordinates to ensure full route is visible
    if (displayRoutes.length > 0) {
      displayRoutes.forEach((routeObj) => {
        const routeData = routeObj.route || routeObj;
        if (routeData && Array.isArray(routeData)) {
          routeData.forEach((coord) => {
            if (coord && Array.isArray(coord) && coord.length >= 2) {
              allPoints.push(coord);
            }
          });
        }
      });
    }

    return allPoints.length > 0 ? allPoints : null;
  }, [displayMarkers, displayRoutes]);

  if (!isClient) {
    return (
      <LoadingContainer>
        <Typography variant="body1">Loading map...</Typography>
      </LoadingContainer>
    );
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Typography variant="body1">Loading map...</Typography>
      </LoadingContainer>
    );
  }

  if (error || !bounds || bounds.length === 0) {
    return (
      <ErrorContainer>
        <ErrorText>{error || "Could not load map"}</ErrorText>
        {startLocation && endLocation && (
          <ErrorDetail>
            <Typography variant="body2" component="div">
              <strong>From:</strong> {startLocation}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>To:</strong> {endLocation}
            </Typography>
          </ErrorDetail>
        )}
      </ErrorContainer>
    );
  }

  // Calculate center point
  const center =
    bounds.length > 0
      ? [
          bounds.reduce((sum, coord) => sum + coord[0], 0) / bounds.length,
          bounds.reduce((sum, coord) => sum + coord[1], 0) / bounds.length,
        ]
      : [-25.7479, 28.2293]; // Default to South Africa center

  // Create custom icons based on color/type
  const createIcon = (color = "blue") => {
    const colorMap = {
      red: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
      green:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
      blue: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
      yellow:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
      orange:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
      violet:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png",
      grey: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png",
      gold: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png",
    };

    return new L.Icon({
      iconUrl: colorMap[color] || colorMap.blue,
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  };

  return (
    <MapContainer
      center={center}
      zoom={10} // Default zoom, will be overridden by fitBounds
      style={{ height: "100%", width: "100%", borderRadius: "8px" }}
      scrollWheelZoom={true}
      whenReady={(mapInstance) => {
        // Ensure bounds are fitted when map is ready
        if (bounds && bounds.length > 0 && autoFitBounds) {
          try {
            const leafletBounds = L.latLngBounds(bounds);
            mapInstance.target.fitBounds(leafletBounds, {
              padding: [80, 80],
              maxZoom: 15,
              animate: false, // No animation on initial load
            });
          } catch (err) {
            logError("Error fitting bounds on map ready", {
              error: err.message,
            });
          }
        }
      }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      <MapBoundsFitter
        bounds={bounds}
        drawerExpanded={drawerExpanded}
        autoFitBounds={autoFitBounds}
      />
      {displayMarkers.map((marker, index) => {
        if (!marker.coords) return null;

        const icon = marker.icon
          ? typeof marker.icon === "string"
            ? createIcon(marker.icon)
            : marker.icon
          : createIcon(marker.color || "blue");

        return (
          <Marker
            key={index}
            position={marker.coords}
            icon={icon}
            eventHandlers={{
              click: () => {
                if (onMarkerClick) {
                  onMarkerClick(marker, index);
                }
              },
            }}
          >
            {marker.popup && (
              <Popup>
                <Box>
                  {marker.label && (
                    <Typography
                      variant="subtitle2"
                      component="strong"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {marker.label}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {typeof marker.popup === "string"
                      ? marker.popup
                      : marker.popup}
                  </Typography>
                </Box>
              </Popup>
            )}
          </Marker>
        );
      })}
      {displayRoutes.map((routeObj, index) => {
        const routeData = routeObj.route || routeObj;
        const routeColor = routeObj.color || "#1890ff";

        if (!routeData || !Array.isArray(routeData)) return null;

        return (
          <Polyline
            key={index}
            positions={routeData}
            color={routeColor}
            weight={4}
            opacity={0.7}
          />
        );
      })}
    </MapContainer>
  );
};

export default RouteMapLeaflet;
